import { Alert, Box, Button, CircularProgress, Grid, Link, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { type PortalFacilityHours, type PortalServiceProviderSearchResult, type PortalLocation, type ReferralTimeFrame } from '../../../core/types'
import { sendGet } from '../../../hooks/use-fetch'
import { Autocomplete } from '../../../Components/AutoComplete'
import { MapModule, useBingMaps } from '../../../hooks/use-bingMaps'
import { FormatTimeAs12Hour } from '../../../core/Utilities'
import { CustomLabelWithToolTip } from '../../../Components/CustomLabel/Index'
import { useScrollTop } from '../../../hooks/use-scrollTop'
import { SelectableRow } from '../InterestForm/SelectableRow'
import { type ServiceProviderOption } from './ServiceProviderMapSelect'
import MaxProvidersSelectedAlert from './MaxProvidersSelectedAlert'

interface ServiceProviderListSelectProps {
  childDOB: Date | null
  childLocation: PortalLocation
  timeFrame: ReferralTimeFrame | null
  isdId: number
  serviceId: number
  isdServiceProviderId: number | null
  maxProviders?: number
  currentEnrollmentCount?: number
  handleBack: () => void
  handleContinue: (selectedProviders: ServiceProviderOption[]) => void
  handleMapSelectionClick: () => void
}

export function ServiceProviderListSelect (props: ServiceProviderListSelectProps): JSX.Element {
  const { isLoaded, getLocationFromAddress, distanceBetweenPoints } = useBingMaps({ modules: [MapModule.SpatialMath] })
  const [providers, setProviders] = useState<ServiceProviderOption[]>([])
  const [providerDataLoaded, setProviderDataLoaded] = useState(false)
  const [selectedProviders, setSelectedProviders] = useState<ServiceProviderOption[]>([])
  const [refreshToken, setRefreshToken] = useState(0)
  const [showNoProviderMessage, setShowNoProviderMessage] = useState(false)

  const remainingEnrollmentsUnderMax = (props.maxProviders ?? 0) - (props.currentEnrollmentCount ?? 0)
  // if max is null, allow all, otherwise take up to the total new + existing
  const maxProvidersReached = props.maxProviders !== undefined && selectedProviders.length >= remainingEnrollmentsUnderMax
  useScrollTop()

  const fetchData = async (): Promise<void> => {
    let queryString = ''
    if
    (props.childDOB != null && props.timeFrame != null) {
      queryString = `?birthDate=${props.childDOB.toString()}&timeFrameType=${props.timeFrame}`
    }
    const { response, success } = await sendGet(`/CaregiverPortal/GetAllProviders/${props.isdId}/ForService/${props.serviceId}/ISDServiceProvider/${props.isdServiceProviderId ?? 0}` +
    queryString, {})
    if (success && window.Microsoft !== undefined && isLoaded) {
      const childLocation = await childMapLocation()

      const providerOptions = [] as ServiceProviderOption[]
      for (const provider of response as PortalServiceProviderSearchResult[]) {
        const distance = await distanceBetween(childLocation, provider)
        const sundayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Sunday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Sunday' }
        const mondayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Monday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Monday' }
        const tuesdayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Tuesday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Tuesday' }
        const wednesdayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Wednesday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Wednesday' }
        const thursdayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Thursday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Thursday' }
        const fridayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Friday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Friday' }
        const saturdayHours = provider.facilityHours.find(h => h.dayOfWeek === 'Saturday') ?? { openTime: '', closeTime: '', dayOfWeek: 'Saturday' }
        const times = [sundayHours, mondayHours, tuesdayHours, wednesdayHours, thursdayHours, fridayHours, saturdayHours] as PortalFacilityHours[]
        providerOptions.push({
          id: provider.facilityID,
          name: `${provider.serviceProviderName} - ${provider.facilityName}`,
          subtext: distance,
          distance,
          times,
          milesTo: 0,
          addressLine: '',
          todayHours: '',
          phoneNumber: null,
          website: null,
          qualityRating: '',
          offeringNarrative: '',
          providesBeforeAfterSchoolCare: false,
          programs: [],
          zipCode: null
        })
      }

      setProviders(providerOptions)
      setProviderDataLoaded(true)
      if (providerOptions.length === 0) {
        setShowNoProviderMessage(true)
      }

      // const tempProviders = [] as ServiceProviderOption[]
      // if (currentService != null) {
      //  for (const provider of providerOptions) {
      //    if (currentService.facilityIds.includes(provider.id)) {
      //      tempProviders.push(provider)
      //    }
      //  }
      // }
      // setSelectedProviders(tempProviders)
    }
  }

  useEffect(() => {
    void fetchData()
  }, [props.isdId, isLoaded, props.serviceId, props.childLocation])

  const childMapLocation = async (): Promise<Microsoft.Maps.Location | null> => {
    return await getLocationFromAddress({
      address: props.childLocation?.address,
      city: props.childLocation?.city,
      state: props.childLocation?.state,
      zipCode: props.childLocation?.zipCode
    })
  }

  const distanceBetween = async (childLocation: Microsoft.Maps.Location | null, provider: PortalServiceProviderSearchResult): Promise<string> => {
    const providerLocation = await getLocationFromAddress({
      latitude: provider.latitude,
      longitude: provider.longitude,
      address: provider.address,
      city: provider.city,
      state: provider.state,
      zipCode: provider.zipCode
    })

    if (childLocation == null || providerLocation == null) {
      return 'Unknown distance'
    }

    const milesTo = distanceBetweenPoints(childLocation, providerLocation)

    return milesTo.toFixed(1) + ' miles away'
  }

  const handleSelect = (e: any, value: { id: string | number, name: string } | null): void => {
    const provider = providers.find(p => p.id === value?.id)
    if (provider == null || maxProvidersReached) {
      return
    }

    const tempProviders = [...selectedProviders]
    const existingProviderIndex = tempProviders.findIndex(p => p.id === provider.id)
    if (existingProviderIndex === -1) {
      tempProviders.push(provider)
      setSelectedProviders(tempProviders)
      setRefreshToken(refreshToken + 1)
    }
  }

  const handleUnselect = (id: number): void => {
    const tempProviders = [...selectedProviders]
    const existingProviderIndex = tempProviders.findIndex(p => p.id === id)
    if (existingProviderIndex !== -1) {
      tempProviders.splice(existingProviderIndex, 1)
      setSelectedProviders(tempProviders)
      setRefreshToken(refreshToken + 1)
    }
  }

  const handleContinue = async (): Promise<void> => {
    if (selectedProviders.length === 0) {
      toast.error('Please select a provider to continue')
      return
    }

    props.handleContinue(selectedProviders)
  }

  return <>
    <Grid container spacing={2} >
      <Grid item xs={12}>
        <Typography variant='h5' sx={{ mb: '30px' }} component='div'>
          <CustomLabelWithToolTip
            name='IF_Service_Search_Intro'
            isdId={props.isdId ?? 0}
            defaultLabelText='Great news! Where would you like to go?'
          />
        </Typography>
        <Typography variant='subtitle1' sx={{ mb: '10px' }} component='div'>
          Want to browse providers in the area? Go <Link sx={{ cursor: 'pointer' }} onClick={props.handleMapSelectionClick} data-testid='browse-providers'>here</Link> instead.
        </Typography>
        {maxProvidersReached &&
          <MaxProvidersSelectedAlert maxProviders={props.maxProviders} currentCount={props.currentEnrollmentCount} />
        }
        {showNoProviderMessage &&
          <Box sx={{ p: '10px 0 3px 0' }}>
            <Alert severity="error" sx={{ border: 1, borderColor: 'red' }}>
              Based on your child's age, it appears there are no providers in your area.
            </Alert>
          </Box>
        }
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          key={refreshToken}
          width='100%'
          label='Service Provider'
          name='serviceProvider'
          onChange={handleSelect}
          options={providers}
          value={0}
          placeholder="Search providers..."
          noBottomPadding
          dataTestId='service-provider'
          disabled={maxProvidersReached}
        />
        {!providerDataLoaded && (
          <CircularProgress />
        )}
      </Grid>

      {selectedProviders.length > 0 &&
        <Grid item xs={12}>
          {selectedProviders.map((provider) => {
            return <SelectableRow
              key={provider.id}
              content={<Box>
                {provider.name}
                {provider.times.map((time) => {
                  return <Box key={time.dayOfWeek}>
                    <Typography variant='body1' sx={{ mt: '10px', display: 'flex', justifyContent: 'space-between', width: '250px' }} component='div'>
                      <Box>{time.dayOfWeek}</Box>
                      <Box sx={{ width: '135px' }}>{time.openTime !== '' ? `${FormatTimeAs12Hour(time.openTime)} - ${FormatTimeAs12Hour(time.closeTime)}` : 'Closed'}</Box>
                    </Typography>
                  </Box>
                })}
                <Typography variant='body1' sx={{ mt: '10px' }} component='div'>
                  {provider.distance}
                </Typography>
              </Box>
              }
              id={provider.id}
              selectedId={provider.id}
              setSelected={handleUnselect}
            />
          })}
        </Grid>
      }

      <Grid item xs={6} sx={{ mb: '20px', mt: '20px' }}>
        <Button
          name='backButton'
          className='back-button'
          data-testid='back-button'
          onClick={props.handleBack}
          variant='outlined'
        >
          Back
        </Button>
      </Grid>
      <Grid item xs={6} sx={{ mb: '20px', mt: '20px' }}>
        <Button
          name='continueButton'
          className='footer-button'
          data-testid='continue-button'
          onClick={handleContinue}
          variant='contained'
        >
          Continue
        </Button>
      </Grid>
    </Grid>
  </>
}
